import { Button, Container, IconButton, makeStyles, Tooltip } from '@material-ui/core';
import clsx from 'clsx';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { MdClose } from 'react-icons/md';
import { useHistory } from 'react-router-dom';
import { InputField } from '../../components/ui/InputField';
import { Logo } from '../../components/ui/Logo';
import { PlanEatUserContext, ApplicationUserContext } from '../../config/Context';
import { SIGN_UP_CREATE_COMPANY, SIGN_UP_JOIN_COMPANY } from '../../constants/signUpInfoTypes';
import { IServiceUserContent } from '../../datasolvr/ServicesFacade';
import { planEatApi } from '../../planEatApi/PlanEatApi';
import { IEaterGroupContent, IUserContent } from '../../planEatApi/PlanEatFacade';
import { extractErrorMessage } from '../../components/utils/ErrorHandler';

const useStyles = makeStyles((theme: any) => ({
    container:{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column"
    },
    abortSignUpRow:{
        display: 'flex',
        justifyContent: 'flex-start',
        width: "100%",
        padding: 15
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        width: '400px',
        '& > *':{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minWidth: '100%'
        }
    },
    formContent:{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minWidth: '100%',
        flexDirection: 'column'
    },
    title: {
        marginTop: '2%',
        marginBottom: 28,
        fontSize: 20,
        fontWeight: 700
    },
    inputContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        paddingTop: 15,
    },
    signUpTypeContainer: {
        display: 'flex',
        minHeight: 80,
        width: '100%',
        float: 'left'
    },
    typeContainer: {
        display: 'grid',
        gridTemplateColumns: '100%',
        fontWeight: 700,
        cursor: 'pointer',
        width: 'calc(100%/2)',
        padding: 8,
        opacity: 0.6,
        marginBottom: 10,
        "&:last-child": {
            borderRadius: '0px 8px 8px 0px'
        },
        "&:first-child": {
            borderRadius: '8px 0px 0px 8px'
        },
        border: '1px solid black'
    },
    fontTypeStyle: {
        fontSize: '80%',
        fontWeight: 400
    },
    buttonContainer: {
        color: 'white',
        fontSize: 12,
        padding: 10,
        fontWeight: 700,
        letterSpacing: 2,
        marginTop: 30,
        marginBottom: 50
    },
    selected:{
        color: 'blue',
        borderColor: 'blue',
        opacity: 1,
        backgroundColor: 'rgba(76,39,245,0.1)'
    }
}));


export const SignUpInfo: React.FC = () => {
    const classes = useStyles();
    let history = useHistory();

    // Context
    const planEatUserContext = useContext(PlanEatUserContext);
    const applicationUserContext = useContext(ApplicationUserContext);
    
    // State
    const [applicationName, setApplicationName] = useState("")
    const [selectedType, setSelectedType] = useState(SIGN_UP_CREATE_COMPANY);
    const [applicationUser, setApplicationUser] = useState<IServiceUserContent>()
    const [planeatUser, setPlaneatUser] = useState<IUserContent>()
    const [eaterGroup, setEaterGroup] = useState<IEaterGroupContent>({
        id: "",
        name: "",
        description: "",
        officeId: "",
        eaterGroupName: "",
        eaterGroupCVR: "",
        eaterGroupAddress: "",
        eaterGroupPhoneNumber: "",
        eaterGroupAdminIds: [],
    });

    useEffect(() => {
        if(applicationUserContext?.applicationServiceUser && applicationUserContext.applicationServiceUser.content.id !== applicationUser?.id){
            setApplicationUser(applicationUserContext.applicationServiceUser.content)
        }
    },[applicationUserContext])

    useEffect(() => {
        if(planEatUserContext?.planEatServiceUser && planEatUserContext.planEatServiceUser.content.id !== applicationUser?.id){
            setPlaneatUser(planEatUserContext.planEatServiceUser.content)
        }
    },[planEatUserContext])

    function createCompany(){
        if(applicationUser && planeatUser){
            if(isEaterGroupValid() && isApplicationUserValid(applicationUser)){
                postEaterGroup(planeatUser)
            }
        }
    }

    function joinCompany(){
        if(applicationUser && planeatUser){
            if(isApplicationUserValid(applicationUser)){
                postPlaneatUser(setPlaneatParams(planeatUser,eaterGroup.id,applicationUser.id)) 
            }    
        }
    }

    function setPlaneatParams(tmpPlaneatUser: IUserContent, eaterGroupId: string, applicationUserId: string){
        return {
            id: tmpPlaneatUser.id,
            name: tmpPlaneatUser.name,
            description: tmpPlaneatUser.description,
            applicationUserId: applicationUserId,
            officeId: eaterGroup.officeId,
            eaterGroupId: eaterGroupId
        }
    }

    function postEaterGroup(tmpPlaneatUser: IUserContent){
        if(applicationUser){
            planEatApi.postEaterGroup(eaterGroup)
            .then((eaterGroupResponse) => {
                postPlaneatUser(setPlaneatParams(tmpPlaneatUser,eaterGroupResponse.data.content.id,applicationUser.id))
            })
            .catch((e) => {
                extractErrorMessage(e, "Failed creating company")
            })
        }
    }

    function postPlaneatUser(tmpPlaneatUser: IUserContent){
        planEatApi.postUser(tmpPlaneatUser)
        .then(response => {
            planEatUserContext?.setPlanEatServiceUser(response.data)
            postApplicationServiceUser()
        }).catch(e => {
            extractErrorMessage(e, "Failed creating Planeat user")
        })
    }

    function postApplicationServiceUser(){
        if(applicationUser){
            planEatApi.getServiceUser(applicationUser.id)
            .then(response => {
                response.data.content.name = applicationName
                planEatApi.postServiceUser(response.data.content)
                .then(() => {
                    history.push("/booking")
                }).catch(e => {
                    extractErrorMessage(e, "Failed creating Service User")
                })
            })
            .catch((e) => {
                console.log(e)
            })
        }
    }

    function isEaterGroupValid(){
        if(eaterGroup.eaterGroupName.length <= 2){
            toast.error("Company name should be at least 3 characters long")
            return false
        } else if(eaterGroup.eaterGroupAddress.length <= 0){
            toast.error("Please enter a company address")
            return false
        } else if(eaterGroup.eaterGroupPhoneNumber.length < 6){
            toast.error("Please enter a company phone number")
            return false
        } else {
            return true
        }
    }

    function isApplicationUserValid(tmpApplicationUser: IServiceUserContent){
        if(applicationName.length < 2){
            toast.error("Your name should be at least 3 characters long")
            return false
        } else {
            return true
        }
    }

    function abortSignUp(){
        localStorage.clear()
        history.push("")
    }

    const handleEaterGroupChange = (prop: keyof IEaterGroupContent) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setEaterGroup({ ...eaterGroup, [prop]: event.target.value });
    };
    

    return (
        <div className={classes.container}>
            <div className={classes.abortSignUpRow}>
                <Tooltip title="Abort sign up">
                    <IconButton onClick={() => abortSignUp()}> 
                        <MdClose />
                    </IconButton>
                </Tooltip>
            </div>
            <div className={classes.content}>
                <Logo variant='normal' />   
                <div className={classes.title}>
                    User information
                </div>
                {
                    applicationUser && planeatUser && 
                    <div className={classes.formContent}>
                    <div>
                        <div className={classes.signUpTypeContainer}>
                            <div className={clsx(classes.typeContainer,selectedType === SIGN_UP_CREATE_COMPANY && classes.selected)} onClick={() => { setSelectedType(SIGN_UP_CREATE_COMPANY) }}>
                                <div>
                                    <p>
                                        Create Company account
                                    </p>
                                    <p className={classes.fontTypeStyle}>
                                        I want to create a Company or individual account
                                    </p>
                                </div>
                            </div>
                            <div className={clsx(classes.typeContainer,selectedType === SIGN_UP_JOIN_COMPANY && classes.selected)} onClick={() => { setSelectedType(SIGN_UP_JOIN_COMPANY) }}>
                                <div>
                                    <p>
                                        Join Company account
                                    </p>
                                    <p className={classes.fontTypeStyle}>
                                        I want to be added to an exisiting Company account.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className={classes.inputContainer}>
                            <InputField hidden={selectedType === SIGN_UP_CREATE_COMPANY ? false : true}
                                fullWidth
                                variant='outlined'
                                id="officeId"
                                label="Office code"
                                name="officeId"
                                helperText='Enter office code provided by management'
                                value={eaterGroup.officeId}
                                onChange={handleEaterGroupChange('officeId')}
                                style={{marginBottom: 15}}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                required/>
                            <InputField hidden={selectedType === SIGN_UP_CREATE_COMPANY ? false : true}
                                fullWidth
                                variant='outlined'
                                id="groupCompany"
                                label="Company/Business name"
                                name="groupCompany"
                                value={eaterGroup.eaterGroupName}
                                onChange={handleEaterGroupChange('eaterGroupName')} 
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                required/>
                            <InputField hidden={selectedType === SIGN_UP_CREATE_COMPANY ? false : true}
                                margin="normal"
                                fullWidth
                                id="groupCvr"
                                label="Company CVR"
                                name="groupCvr"
                                autoComplete="groupCvr"
                                variant="outlined"
                                helperText='If you don´t have a CVR number, stay blank'
                                value={eaterGroup.eaterGroupCVR}
                                onChange={handleEaterGroupChange('eaterGroupCVR')}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                required/>
                            <InputField hidden={selectedType === SIGN_UP_CREATE_COMPANY ? false : true}
                                margin="normal"
                                fullWidth
                                id="groupAddress"
                                label="Company Address"
                                name="groupAddress"
                                autoComplete="groupAddress"
                                variant="outlined"
                                value={eaterGroup.eaterGroupAddress}
                                onChange={handleEaterGroupChange('eaterGroupAddress')}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                required/>
                            <InputField hidden={selectedType === SIGN_UP_CREATE_COMPANY ? false : true}
                                margin="normal"
                                fullWidth
                                id="groupPhoneNumber"
                                label="Company Phone Number"
                                name="groupPhoneNumber"
                                autoComplete="groupPhoneNumber"
                                variant="outlined" 
                                value={eaterGroup.eaterGroupPhoneNumber}
                                onChange={handleEaterGroupChange('eaterGroupPhoneNumber')}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                required/>
                            <InputField hidden={selectedType === SIGN_UP_JOIN_COMPANY ? false : true}
                                margin="normal"
                                fullWidth
                                id="joinGroup"
                                label="Company account code"
                                name="joinGroup"
                                autoComplete="joinGroup"
                                value={eaterGroup.id}
                                onChange={handleEaterGroupChange('id')}
                                variant="outlined"
                                helperText='Your Group admin can supply with a group code.' 
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                required/>
                            <InputField
                                margin="normal"
                                fullWidth
                                id="fullName"
                                label="Your full name"
                                name="fullName"
                                variant="outlined"
                                value={applicationName}
                                onChange={(e) => {setApplicationName(e.target.value)}} 
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                required/>
                        </div>
                        {
                            selectedType === SIGN_UP_CREATE_COMPANY ?
                            <Button
                                fullWidth
                                variant="contained"
                                className={classes.buttonContainer}
                                onClick={createCompany}
                            >
                                Create Company account
                            </Button>
                            : selectedType === SIGN_UP_JOIN_COMPANY ?
                            <Button
                                fullWidth
                                variant="contained"
                                className={classes.buttonContainer}
                                onClick={joinCompany}
                            >
                                Join Company account
                            </Button>
                            :
                            <div/>
                        }
                    </div>
                </div>
                }
            </div>
        </div>
    )
}